import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Collapse,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import type { ExplorerSystem } from "../types";

function matches(text: string | null, needle: string): boolean {
  return (text ?? "").toLowerCase().includes(needle);
}

/** A system stays listed when its own name matches, even with none of its drawings. */
function filterSystems(
  systems: ExplorerSystem[],
  needle: string,
): ExplorerSystem[] {
  if (!needle) return systems;
  return systems
    .map((system) =>
      matches(system.name, needle) || matches(system.system_type, needle)
        ? system
        : {
            ...system,
            pids: system.pids.filter(
              (pid) =>
                matches(pid.filename, needle) ||
                matches(pid.sub_system, needle),
            ),
          },
    )
    .filter((system) => matches(system.name, needle) || system.pids.length > 0);
}

export default function SystemPidPanel({
  systems,
  selectedSystemId,
  selectedPidId,
  onSelectSystem,
  onSelectPid,
}: {
  /** Undefined while the tree of the selected plant is still loading. */
  systems: ExplorerSystem[] | undefined;
  selectedSystemId: string | null;
  selectedPidId: string | null;
  onSelectSystem: (systemId: string) => void;
  onSelectPid: (pidId: string) => void;
}) {
  const [search, setSearch] = useState("");
  /** The open system can be folded back without losing the drawing it holds. */
  const [collapsedId, setCollapsedId] = useState<string | null>(null);

  const needle = search.trim().toLowerCase();
  const filtered = filterSystems(systems ?? [], needle);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: 0,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Systems & Drawings
      </Typography>
      <TextField
        size="small"
        placeholder="Search systems and drawings..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <Box
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
      >
        {!systems ? (
          <Skeleton variant="rounded" height={160} />
        ) : filtered.length === 0 ? (
          <Alert severity="info" sx={{ py: 0 }}>
            {needle
              ? "Nothing matches this search."
              : "No drawing of this plant carries a SPOF yet."}
          </Alert>
        ) : (
          <List dense disablePadding>
            {filtered.map((system) => {
              // A search opens every match; otherwise the selected system unfolds, and the
              // first one does while nothing is selected yet.
              const unfoldedId = selectedSystemId ?? filtered[0].id;
              const open = needle
                ? true
                : system.id === unfoldedId && collapsedId !== system.id;
              return (
                <Box key={system.id}>
                  <ListItemButton
                    selected={system.id === selectedSystemId}
                    onClick={() => {
                      setCollapsedId(
                        system.id === unfoldedId && open ? system.id : null,
                      );
                      // Re-selecting would drop the drawing opened from this system.
                      if (system.id !== selectedSystemId) {
                        onSelectSystem(system.id);
                      }
                    }}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={system.name}
                      secondary={system.system_type}
                      sx={{ minWidth: 0, wordBreak: "break-word" }}
                      slotProps={{
                        primary: { sx: { fontSize: 13, fontWeight: 600 } },
                        secondary: { sx: { fontSize: 11 } },
                      }}
                    />
                    {open ? (
                      <ExpandLessIcon fontSize="small" sx={{ ml: 0.5 }} />
                    ) : (
                      <ExpandMoreIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </ListItemButton>

                  <Collapse in={open} unmountOnExit>
                    <List dense disablePadding sx={{ pl: 1.5 }}>
                      {system.pids.map((pid) => (
                        <ListItemButton
                          key={pid.id}
                          selected={pid.id === selectedPidId}
                          onClick={() => onSelectPid(pid.id)}
                          sx={{ borderRadius: 1 }}
                        >
                          <ListItemText
                            primary={pid.filename}
                            secondary={pid.sub_system}
                            sx={{ minWidth: 0, wordBreak: "break-word" }}
                            slotProps={{
                              primary: { sx: { fontSize: 13 } },
                              secondary: { sx: { fontSize: 11 } },
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
}
